// 🆕 2026-09-01 · Feld-Fit-Vorschlag (Prozess-Soll Punkt 3).
//
// Das System rechnet den Grid-Search selbst (Auto-Trigger nach
// Anker-Änderungen, sobald ≥5 überbestimmte Anker existieren) und legt
// das Ergebnis VOR — angewandt wird nie still, denn die Feldmaße sind
// die unterste Fehlerschicht unter allem (Systemkarte #1/#2, gemessen:
// die USAU-Differenz allein ≈ 8 px).
//
// Die Meldeschwelle ist streng (bewerte_vorschlag, 11 Tests): das
// Referenzvideo lieferte am 01.09. W=33,2 aus 3 Vorschlägen mit 4,4 m
// Spanne — solches Rauschen bleibt still. Erscheint hier also eine
// Karte, steckt echte, übereinstimmende Beweislage dahinter.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adaptFieldDims, fehlerText, fieldFitVorschlagAblehnen,
  getFieldFitVorschlag, pollJob,
} from "../../lib/api";

export default function FieldFitVorschlag({ seqId, refreshKey = 0, onApplied }) {
  const [vorschlag, setVorschlag] = useState(null);
  const [laufend, setLaufend] = useState(false);
  const [meldung, setMeldung] = useState("");

  const laden = useCallback(() => {
    if (!seqId) return;
    getFieldFitVorschlag(seqId)
      .then((d) => setVorschlag(d?.vorschlag || null))
      .catch(() => setVorschlag(null));
  }, [seqId]);

  useEffect(() => { laden(); }, [laden, refreshKey]);

  if (!vorschlag) return null;
  const offen = vorschlag.status === "abweichung" && !vorschlag.entschieden;
  if (!offen) return null; // bestätigt/still/entschieden zeigt die Pipeline-Kette

  const dims = vorschlag.dims || {};
  const teile = ["W", "L_mid"]
    .filter((d) => dims[d]?.urteil === "abweichung")
    .map((d) => `${d === "W" ? "Breite" : "Mittelfeld-Länge"} ${dims[d].median} m statt ${dims[d].preset} m`);

  const uebernehmen = async () => {
    setLaufend(true);
    setMeldung("Feld-Fit wird angewandt — Anker werden neu gelöst …");
    try {
      const { job_id } = await adaptFieldDims(seqId, { apply: true });
      await pollJob(job_id, {
        onProgress: ({ message }) => setMeldung(message || "läuft …"),
      });
      toast.success("Feldmaße übernommen — alle Anker neu gelöst");
      laden();
      onApplied?.();
    } catch (e) {
      toast.error(fehlerText(e, "Übernahme fehlgeschlagen"));
    } finally {
      setLaufend(false);
      setMeldung("");
    }
  };

  const ablehnen = async () => {
    setLaufend(true);
    try {
      await fieldFitVorschlagAblehnen(seqId);
      toast.success("Vorschlag abgelehnt — Preset-Maße bleiben");
      laden();
    } catch (e) {
      toast.error(fehlerText(e, "Ablehnen fehlgeschlagen"));
    } finally {
      setLaufend(false);
    }
  };

  return (
    <div
      data-testid="field-fit-vorschlag"
      className="rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 space-y-1"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-amber-300">
        📐 Feld-Fit-Vorschlag · aus {vorschlag.n_valid} übereinstimmenden Ankern
      </div>
      <div className="text-[11px] text-amber-100/90">
        Das real gesteckte Feld weicht vom Profil ab: {teile.join(" · ")}
        {vorschlag.reproj_median_px != null
          ? ` (Restfehler-Median ${vorschlag.reproj_median_px} px)` : ""}
      </div>
      <div className="flex gap-2">
        <button
          data-testid="field-fit-uebernehmen"
          disabled={laufend}
          onClick={uebernehmen}
          className="rounded border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
          title="Maße in game_config übernehmen und ALLE Anker mit den neuen Maßen neu lösen (derselbe Job wie der bisherige Knopf, apply=true)."
        >
          ✓ Übernehmen
        </button>
        <button
          data-testid="field-fit-ablehnen"
          disabled={laufend}
          onClick={ablehnen}
          className="rounded border border-slate-500/50 bg-slate-500/10 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-500/20 disabled:opacity-50"
          title="Preset-Maße behalten. Der Vorschlag gilt als entschieden und meldet sich erst wieder, wenn deutlich mehr Anker eine neue Beweislage ergeben."
        >
          ✕ Ablehnen
        </button>
        {laufend && meldung && (
          <span className="text-[10px] text-amber-200/70 font-mono">{meldung}</span>
        )}
      </div>
    </div>
  );
}
